import apiClient from './apiClient';
import type {
    ApiResponse,
    PaginatedResponse,
    Doctor,
    CreateDoctorRequest,
} from '../types/api';

export const doctorService = {
    /**
     * Get paginated list of doctors
     */
    async getAll(perPage = 15): Promise<PaginatedResponse<Doctor>> {
        const response = await apiClient.get<any>(`/doctors?per_page=${perPage}`);
        const raw = response.data;
        return {
            data: raw.data || [],
            total: raw.total ?? 0,
            per_page: raw.per_page ?? perPage,
            current_page: raw.current_page ?? 1,
            last_page: raw.last_page ?? 1,
        };
    },

    /**
     * Get doctor by ID
     */
    async getById(id: number): Promise<Doctor> {
        const response = await apiClient.get<ApiResponse<Doctor>>(`/doctors/${id}`);
        return response.data.data;
    },

    /**
     * Get doctor with schedule
     */
    async getWithSchedule(id: number): Promise<Doctor> {
        const response = await apiClient.get<ApiResponse<Doctor>>(`/doctors/${id}/schedule`);
        return response.data.data;
    },

    /**
     * Get doctors by specialty
     */
    async getBySpecialty(specialtyId: number): Promise<Doctor[]> {
        const response = await apiClient.get<ApiResponse<Doctor[]>>(`/doctors/specialty/${specialtyId}`);
        return response.data.data;
    },

    /**
     * Search doctors by name or alias
     */
    async search(term: string): Promise<Doctor[]> {
        const response = await apiClient.get<ApiResponse<Doctor[]>>(`/doctors/search?term=${encodeURIComponent(term)}`);
        return response.data.data;
    },

    /**
     * Create new doctor
     */
    async create(data: CreateDoctorRequest): Promise<Doctor> {
        const response = await apiClient.post<ApiResponse<Doctor>>('/doctors', data);
        return response.data.data;
    },

    /**
     * Update doctor
     */
    async update(id: number, data: Partial<CreateDoctorRequest>): Promise<void> {
        await apiClient.put(`/doctors/${id}`, data);
    },

    /**
     * Delete doctor
     */
    async delete(id: number): Promise<void> {
        await apiClient.delete(`/doctors/${id}`);
    },
};

export default doctorService;
